import { format, startOfWeek, addDays, isSameDay } from 'date-fns';
import { Cloud, AlertTriangle, Loader2, Wind } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../services/api';
import { WeatherIcon } from './WeatherIcon';
import { mapConditionToIcon } from './weatherCondition';
import type { WeatherIconKind } from './weatherCondition';

// Days of the week starting from Sunday
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Backend API response types
type WeatherHistoryDay = {
  date: string;
  rainfall_inches: number;
  temperature_f: number | null;
  conditions: string | null;
  wind_speed_mph: number | null;
  inspection_required: boolean;
};

type WeatherHistoryResponse = {
  status: string;
  data: {
    site_id: string;
    site_name: string;
    weather_history: WeatherHistoryDay[];
    current_weather: {
      temperature_f: number;
      conditions: string;
      precipitation_inches: number;
    } | null;
    summary: {
      total_rainfall_inches: number;
      inspection_days: number;
      days_with_data: number;
    };
  };
};

interface WeatherWidgetProps {
  siteId?: string;
  // latitude/longitude kept for backwards compatibility but not used when siteId is provided
  latitude?: number;
  longitude?: number;
}

export function WeatherWidget({ siteId }: WeatherWidgetProps) {
  // Fetch weather history from backend API
  const { data: weatherResponse, isLoading } = useQuery({
    queryKey: ['weather-history', siteId],
    queryFn: async () => {
      const response = await api.get<WeatherHistoryResponse>(
        `/weather/site/${siteId}/history?days=7`
      );
      return response.data;
    },
    enabled: !!siteId,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  // Get the current week (Sunday through Saturday)
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 0 }); // Sunday

  // Generate array of dates for current week
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Transform backend data to a map by date for easy lookup
  const getWeatherByDate = () => {
    const weatherMap = new Map<
      string,
      {
        rainfall: number;
        temperature: number | null;
        conditions: WeatherIconKind;
        windSpeed: number;
        inspectionRequired: boolean;
      }
    >();

    if (weatherResponse?.data?.weather_history) {
      weatherResponse.data.weather_history.forEach((day: WeatherHistoryDay) => {
        weatherMap.set(day.date, {
          rainfall: day.rainfall_inches || 0,
          temperature: day.temperature_f,
          conditions: mapConditionToIcon(day.conditions),
          windSpeed: day.wind_speed_mph || 0,
          inspectionRequired: day.inspection_required,
        });
      });
    }

    return weatherMap;
  };

  const weatherMap = getWeatherByDate();

  // Build display data for the current week, using only real data (no mock)
  const displayData = weekDates.map((date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const existing = weatherMap.get(dateKey);

    if (existing) {
      return {
        date: date.toISOString(),
        hasData: true as const,
        ...existing,
      };
    }

    // No data available for this day
    return {
      date: date.toISOString(),
      hasData: false as const,
      rainfall: 0,
      temperature: null,
      conditions: 'unknown' as const,
      windSpeed: 0,
      inspectionRequired: false,
    };
  });

  const getRainfallColor = (inches: number) => {
    if (inches > 1.5) return 'bg-danger-500';
    if (inches > 1.0) return 'bg-warning-500';
    if (inches > 0.5) return 'bg-accent-500';
    if (inches > 0) return 'bg-primary-400';
    return 'bg-slate-200 dark:bg-slate-600';
  };

  // Only count days with actual data
  const daysWithData = displayData.filter((day) => day.hasData);
  const totalRainfall = daysWithData.reduce(
    (sum, day) => sum + day.rainfall,
    0
  );
  const inspectionDays = daysWithData.filter(
    (day) => day.inspectionRequired
  ).length;

  // Show message if no siteId provided
  if (!siteId) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
          <Cloud className="w-5 h-5" />
          <span>Weather data requires a site to be selected</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
      <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Cloud className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            7-Day Weather History
          </h2>
          <div className="flex items-center space-x-4 text-sm">
            <span className="text-slate-500 dark:text-slate-400">
              Total: {totalRainfall.toFixed(2)}"
            </span>
            {inspectionDays > 0 && (
              <span className="text-warning-600 dark:text-warning-400 font-medium flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {inspectionDays} inspection{inspectionDays > 1 ? 's' : ''}{' '}
                triggered
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Calendar Header - Days of Week */}
        <div className="grid grid-cols-7 gap-2 mb-3">
          {WEEKDAYS.map((day, index) => {
            const isTodayColumn = today.getDay() === index;
            return (
              <div
                key={day}
                className={`text-center text-xs font-semibold py-1 rounded ${
                  isTodayColumn
                    ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                {day}
              </div>
            );
          })}
        </div>

        {/* Weather Summary Cards */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-2">
            {displayData.map((day, index) => {
              const dayDate = new Date(day.date);
              const isToday = isSameDay(dayDate, today);

              return (
                <div
                  key={index}
                  className={`text-center p-2 rounded-lg transition-all ${
                    isToday
                      ? 'bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500 dark:ring-primary-400'
                      : 'bg-slate-50 dark:bg-slate-700/50'
                  }`}
                >
                  <p
                    className={`text-sm font-medium mb-2 ${
                      isToday
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-slate-900 dark:text-white'
                    }`}
                  >
                    {format(dayDate, 'M/d')}
                  </p>

                  {day.hasData ? (
                    <>
                      {/* Weather Icon */}
                      <div className="flex justify-center mb-2">
                        <WeatherIcon
                          kind={day.conditions}
                          className="w-8 h-8"
                        />
                      </div>

                      {/* Temperature */}
                      <p
                        className={`text-sm mb-1 ${
                          isToday
                            ? 'text-primary-600 dark:text-primary-300 font-medium'
                            : 'text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        {day.temperature === null
                          ? '—'
                          : `${Math.round(day.temperature)}°`}
                      </p>

                      {/* Wind */}
                      <p
                        className="text-xs mb-1 flex items-center justify-center gap-0.5 text-slate-500 dark:text-slate-400"
                        title={
                          day.windSpeed > 0
                            ? `Wind ${Math.round(day.windSpeed)} mph`
                            : 'No wind data'
                        }
                      >
                        <Wind className="w-3 h-3 shrink-0" />
                        {day.windSpeed > 0 ? Math.round(day.windSpeed) : '—'}
                      </p>

                      {/* Rainfall Bar */}
                      <div className="h-12 w-full bg-slate-100 dark:bg-slate-600 rounded relative">
                        <div
                          className={`absolute bottom-0 left-0 right-0 rounded transition-all ${getRainfallColor(day.rainfall)}`}
                          style={{
                            height: `${Math.min((day.rainfall / 2) * 100, 100)}%`,
                          }}
                        />
                      </div>

                      {/* Rainfall Amount */}
                      <p
                        className={`text-xs mt-1 font-medium ${
                          isToday
                            ? 'text-primary-600 dark:text-primary-300'
                            : 'text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        {day.rainfall.toFixed(1)}"
                      </p>

                      {/* Inspection Required Indicator */}
                      {day.inspectionRequired && (
                        <div className="mt-1">
                          <span
                            className="inline-block w-2 h-2 bg-warning-500 rounded-full"
                            title="Inspection Required"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    // No data placeholder
                    <div className="flex flex-col items-center justify-center h-24">
                      <span className="text-slate-300 dark:text-slate-600 text-2xl">
                        —
                      </span>
                      <span className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                        No data
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
